import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';

import useToast from '../../hooks/useToast';
import './Products.css';
import './ProductsActions.css';
import './MobileResponsive.css';
import '../FullViewLayout.css';
import './ViewSaleModalStyles.css';
import apiService from '../../services/api';
import { exportToCSV, exportToExcel, exportToPDF, printPage } from './exportUtils';
import ConfirmModal from '../ConfirmModal';
import ContactPurchasesModal from './ContactPurchasesModal';
import LedgerModal from './LedgerModal';
import dateUtils from '../../utils/dateUtils';

/* ─── tiny inline helpers ─── */
const DL = ({ label, value, span }) => (
  <div style={{ gridColumn: span ? 'span 2' : 'span 1' }}>
    <div style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', marginBottom: 3 }}>{label}</div>
    <div style={{ fontWeight: 500, color: '#1e293b', fontSize: '0.92rem', wordBreak: 'break-word' }}>{value || <span style={{ color: '#94a3b8' }}>—</span>}</div>
  </div>
);
const SectionHead = ({ children }) => (
  <div style={{ gridColumn: 'span 2', borderBottom: '1px solid #e2e8f0', paddingBottom: '0.35rem', marginTop: '0.5rem', marginBottom: '0.25rem', fontWeight: 700, fontSize: '0.82rem', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{children}</div>
);

const Suppliers = ({ isActive, filterStatus, searchTerm, onSearchChange, onShowFilters }) => {
    const navigate = useNavigate();
    const { showSuccess, showError } = useToast();
    const [suppliers, setSuppliers] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [confirmModal, setConfirmModal] = useState({ isOpen: false, action: '', data: null });

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(25);
    const [totalEntries, setTotalEntries] = useState(0);
    const [lastPage, setLastPage] = useState(1);
    const [isClientPaginated, setIsClientPaginated] = useState(false);

    // Filter States removed (lifted to parent)

    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [openActionId, setOpenActionId] = useState(null);
    const [showExportDropdown, setShowExportDropdown] = useState(false);
    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showPayModal, setShowPayModal] = useState(false);
    const [showLedgerModal, setShowLedgerModal] = useState(false);
    const [showDocModal, setShowDocModal] = useState(false);
    const [showPurchasesModal, setShowPurchasesModal] = useState(false);
    const [payAmount, setPayAmount] = useState('');
    const [fetchTrigger, setFetchTrigger] = useState(0);
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [payNote, setPayNote] = useState('');
    const [docNote, setDocNote] = useState('');

    const handleExport = (format) => {
        if (!suppliers || suppliers.length === 0) {
            alert('No suppliers to export');
            setShowExportDropdown(false);
            return;
        }

        const headers = ['Contact ID', 'Business Name', 'Name', 'Email', 'Mobile', 'Tax Number', 'Credit Limit', 'Balance'];
        const rows = suppliers.map(s => [
            s.contact_id || '', s.business_name || '', s.name || '', s.email || '', s.mobile || '',
            s.tax_number || '', s.credit_limit || '0', s.balance || '0'
        ]);

        const title = 'Suppliers List';
        const filename = 'suppliers';

        if (format === 'CSV') exportToCSV(headers, rows, filename);
        else if (format === 'Excel') exportToExcel(headers, rows, filename);
        else if (format === 'PDF') exportToPDF(title, headers, rows, filename);
        else if (format === 'Print') printPage();

        setShowExportDropdown(false);
    };

    const handlePrint = () => { printPage(); setShowExportDropdown(false); };
    const handleColumnVisibility = () => { alert('Column visibility settings'); setShowExportDropdown(false); };
    const handleExportPDF = () => { handleExport('PDF'); };

    // Debounce search input so API is not hit on every keystroke
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 400);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Reset to page 1 when debounced search or filter changes, then trigger a single fetch
    useEffect(() => {
        setCurrentPage(1);
        setFetchTrigger(t => t + 1);
    }, [debouncedSearchTerm, filterStatus]);

    useEffect(() => {
        if (isActive) {
            fetchSuppliers();
        }
    }, [isActive, currentPage, perPage, fetchTrigger]);

    const fetchSuppliers = async () => {
        setIsLoading(true);
        try {
            const params = {
                type: 'supplier',
                per_page: -1
            };
            if (debouncedSearchTerm.trim()) {
                params.name = debouncedSearchTerm.trim();
            }
            const data = await apiService.getContacts(params);
            const list = Array.isArray(data) ? data : (data.data || []);
            setSuppliers(list);
            setIsClientPaginated(true);
            setTotalEntries(list.length);
            setLastPage(Math.ceil(list.length / perPage));
        } catch (err) {
      /* console.error('Failed to load suppliers', err); */
            setError('Failed to load suppliers');
        } finally {
            setIsLoading(false);
        }
    };

    const filteredSuppliers = useMemo(() => {
        let result = [...suppliers];
        if (filterStatus !== 'all') {
            result = result.filter(s => s.contact_status === filterStatus);
        }
        if (searchTerm.trim()) {
            const lower = searchTerm.toLowerCase();
            result = result.filter(s => {
                const fields = [
                    s.name,
                    s.supplier_business_name,
                    s.business_name,
                    s.email,
                    s.mobile,
                    s.contact_id,
                    s.tax_number,
                    s.city,
                    s.state,
                    s.address_line_1,
                    s.landline,
                    s.alternate_number,
                    s.custom_field1,
                    s.custom_field2,
                    s.custom_field3,
                    s.custom_field4,
                    s.custom_field5
                ];
                return fields.some(f => f && String(f).toLowerCase().includes(lower));
            });
        }
        return result;
    }, [suppliers, searchTerm, filterStatus]);

    const [dropdownPosition, setDropdownPosition] = useState({ top: null, bottom: null, left: 0, maxHeight: null });

    const toggleActionMenu = (id, event) => {
        if (event) event.stopPropagation();
        if (openActionId === id) {
            setOpenActionId(null);
        } else {
            const rect = event ? event.currentTarget.getBoundingClientRect() : { bottom: 0, top: 0, left: 0 };
            const gap = 8;
            const viewportHeight = window.innerHeight;
            const viewportWidth = window.innerWidth;
            const menuMinWidth = 220;
            const edgePadding = 8;

            const spaceBelow = viewportHeight - rect.bottom;
            const spaceAbove = rect.top;

            const openUpwards = spaceBelow < 240 && spaceAbove > spaceBelow;
            const availableHeight = openUpwards ? (spaceAbove - gap - edgePadding) : (spaceBelow - gap - edgePadding);
            const maxHeight = Math.max(160, Math.min(Math.floor(viewportHeight * 0.6), Math.floor(availableHeight)));

            const left = Math.min(
                Math.max(rect.left, edgePadding),
                Math.max(edgePadding, viewportWidth - menuMinWidth - edgePadding)
            );

            setDropdownPosition({
                top: openUpwards ? null : (rect.bottom + gap),
                bottom: openUpwards ? (viewportHeight - rect.top + gap) : null,
                left,
                maxHeight
            });
            setOpenActionId(id);
        }
    };


    const handleViewSupplier = async (supplier) => {
        if (!supplier) return;
        setSelectedSupplier(supplier);
        setShowViewModal(true);
        setOpenActionId(null);
        try {
            const data = await apiService.getCustomerById(supplier.id);
            let detail = data;
            if (data?.data) {
                detail = Array.isArray(data.data) ? data.data[0] : data.data;
            } else if (Array.isArray(data)) {
                detail = data[0];
            }
            if (detail) {
                setSelectedSupplier(prev => ({ ...prev, ...detail }));
            }
        } catch (e) {}
    };

    const handleCloseModal = () => {
        setShowViewModal(false);
        setSelectedSupplier(null);
    };

    const handleEditSupplier = (id) => {
        navigate(`/contacts/suppliers/edit/${id}`);
    };

    const handleDeleteSupplier = (id) => {
        setOpenActionId(null);
        setConfirmModal({ isOpen: true, action: 'delete', data: id });
    };

    const handleDeactivate = (supplier) => {
        setOpenActionId(null);
        setConfirmModal({ isOpen: true, action: 'deactivate', data: supplier });
    };

    const confirmAction = async () => {
        const { action, data } = confirmModal;
        setConfirmModal({ isOpen: false, action: '', data: null });

        if (action === 'delete') {
            try {
                await apiService.deleteContact(data);
                fetchSuppliers();
                showSuccess('Supplier deleted successfully!');
            } catch (err) {
                showError('Failed to delete supplier: ' + err.message);
            }
        } else if (action === 'deactivate') {
            const supplier = data;
            const newStatus = supplier.contact_status === 'inactive' ? 'active' : 'inactive';
            try {
                await apiService.updateContact(supplier.id, { contact_status: newStatus });
                // Update local state immediately so it stays in the list with the updated status
                setSuppliers(prev => prev.map(s => s.id === supplier.id ? { ...s, contact_status: newStatus } : s));
                
                if (newStatus === 'inactive') {
                    showSuccess('Supplier deactivated successfully!');
                } else {
                    showSuccess('Supplier activated successfully!');
                }
            } catch (err) { 
                showError('Failed: ' + err.message); 
            }
        }
    };

    const openPayModal = (supplier) => { setSelectedSupplier(supplier); setPayAmount(''); setPayNote(''); setShowPayModal(true); setOpenActionId(null); };
    const openLedger = (supplier) => { setSelectedSupplier(supplier); setShowLedgerModal(true); setOpenActionId(null); };
    const openDocModal = (supplier) => { setSelectedSupplier(supplier); setDocNote(''); setShowDocModal(true); setOpenActionId(null); };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('.action-dropdown-container')) {
                setOpenActionId(null);
            }
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortedSuppliers = (list) => {
        if (!sortConfig.key) return list;
        return [...list].sort((a, b) => {
            let aVal = a[sortConfig.key] || '';
            let bVal = b[sortConfig.key] || '';
            if (typeof aVal === 'string') aVal = aVal.toLowerCase();
            if (typeof bVal === 'string') bVal = bVal.toLowerCase();

            if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
    };

    return (
        <div className="full-view-inner">
            {/* Table Controls */}
            <div className="table-controls">
                <div className="table-controls-left">
                    <div className="entries-dropdown">
                        <span>Show</span>
                        <select value={perPage} onChange={(e) => {
                            setPerPage(Number(e.target.value));
                            setCurrentPage(1);
                        }}>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                        <span>entries</span>
                    </div>
                </div>

                <div className="table-controls-right">
                    <div className="tabs-search-wrapper" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input
                            type="text"
                            placeholder="Search Suppliers..."
                            value={searchTerm}
                            onChange={(e) => onSearchChange(e.target.value)}
                            className="tabs-search-input"
                        />
                        <button
                            className="filter-toggle-btn"
                            onClick={onShowFilters}
                            title="Open Filters"
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
                            </svg>
                            Filters
                        </button>
                    </div>
                    <div className="export-dropdown-container" style={{ position: 'relative' }}>
                        <button className="export-btn" onClick={() => setShowExportDropdown(!showExportDropdown)}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                <polyline points="7 10 12 15 17 10"></polyline>
                                <line x1="12" y1="15" x2="12" y2="3"></line>
                            </svg>
                            Export ▾
                        </button>
                        {showExportDropdown && (
                            <div className="export-dropdown-menu">
                                <button onClick={() => handleExport('CSV')}><i className="fa fa-file-csv"></i> Export CSV</button>
                                <button onClick={() => handleExport('Excel')}><i className="fa fa-file-excel"></i> Export Excel</button>
                                <button onClick={() => handleExport('Print')}><i className="fa fa-print"></i> Print</button>
                                <button onClick={() => handleExport('PDF')}><i className="fa fa-file-pdf"></i> Export PDF</button>
                            </div>
                        )}
                    </div>
                    <button className="control-btn-primary" onClick={() => navigate('/contacts/suppliers/add')}>
                        <span className="plus-icon">+</span> Add
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="products-table-container">
                    {isLoading ? (
                        <div className="loading-sales">
                            <h3>⏳ Loading Suppliers...</h3>
                            <p>Fetching suppliers data from server...</p>
                        </div>
                    ) : filteredSuppliers.length === 0 ? (
                        <div className="full-view-empty">
                            <h3>📊 No suppliers found</h3>
                            <p>Try adjusting filters or add a new supplier.</p>
                        </div>
                    ) : (
                        <table className="products-table">
                            <thead>
                                <tr>
                                    <th>Action</th>
                                    {[
                                        { key: 'contact_id', label: 'Contact ID' },
                                        { key: 'business_name', label: 'Business Name' },
                                        { key: 'name', label: 'Name' },
                                        { key: 'email', label: 'Email' },
                                        { key: 'tax_number', label: 'Tax number' },
                                        { key: 'pay_term_number', label: 'Pay term' },
                                        { key: 'opening_balance', label: 'Opening Balance' },
                                        { key: 'advance_balance', label: 'Advance Balance' },
                                        { key: 'created_at', label: 'Added On' },
                                        { key: 'address', label: 'Address' },
                                        { key: 'mobile', label: 'Mobile' },
                                        { key: 'total_purchase_due', label: 'Total Purchase Due' },
                                        { key: 'total_purchase_return_due', label: 'Total Purchase Return Due' },
                                        { key: 'custom_field1', label: 'Custom Field 1' },
                                        { key: 'custom_field2', label: 'Custom Field 2' },
                                        { key: 'custom_field3', label: 'Custom Field 3' },
                                        { key: 'custom_field4', label: 'Custom Field 4' },
                                        { key: 'custom_field5', label: 'Custom Field 5' }
                                    ].map(({ key, label }) => (
                                        <th key={key} className="sortable" onClick={() => handleSort(key)}>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
                                                {label}
                                                {sortConfig.key === key ? (
                                                    sortConfig.direction === 'asc' ? (
                                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <path d="M18 15l-6-6-6 6" />
                                                        </svg>
                                                    ) : (
                                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <path d="M6 9l6 6 6-6" />
                                                        </svg>
                                                    )
                                                ) : (
                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M7 15l5 5 5-5" />
                                                        <path d="M7 9l5-5 5 5" />
                                                    </svg>
                                                )}
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {filteredSuppliers.length === 0 ? (
                                    <tr><td colSpan="19" style={{ textAlign: 'center', padding: '2rem' }}>No suppliers found.</td></tr>
                                ) : (
                                    (() => {
                                        const sorted = getSortedSuppliers(filteredSuppliers);
                                        const displayList = sorted.slice((currentPage - 1) * perPage, currentPage * perPage);
                                        return displayList.map(supplier => (
                                            <tr key={supplier.id}>
                                                <td className="actions-cell">
                                                    <div className="action-dropdown-container">
                                                        <button className="action-btn-trigger" onClick={(e) => toggleActionMenu(supplier.id, e)}>
                                                            Actions <span className="arrow-down">▼</span>
                                                        </button>
                                                        {openActionId === supplier.id && ReactDOM.createPortal(
                                                            <div
                                                                className="action-dropdown-menu"
                                                                style={{
                                                                    position: 'fixed',
                                                                    top: dropdownPosition.top === null ? 'auto' : `${dropdownPosition.top}px`,
                                                                    bottom: dropdownPosition.bottom === null ? 'auto' : `${dropdownPosition.bottom}px`,
                                                                    left: `${dropdownPosition.left}px`,
                                                                    maxHeight: dropdownPosition.maxHeight === null ? undefined : `${dropdownPosition.maxHeight}px`,
                                                                    zIndex: 9999999
                                                                }}
                                                            >
                                                                <button onClick={() => openPayModal(supplier)}><i className="fa fa-credit-card" style={{ marginRight: '0.5rem', color: '#6366f1' }}></i> Pay</button>
                                                                <button onClick={() => handleViewSupplier(supplier)}><i className="fa fa-eye" style={{ marginRight: '0.5rem', color: '#3b82f6' }}></i> View</button>
                                                                <button onClick={() => handleEditSupplier(supplier.id)}><i className="fa fa-edit" style={{ marginRight: '0.5rem', color: '#10b981' }}></i> Edit</button>
                                                                <button onClick={() => handleDeleteSupplier(supplier.id)}><i className="fa fa-trash" style={{ marginRight: '0.5rem', color: '#ef4444' }}></i> Delete</button>
                                                                <button onClick={() => handleDeactivate(supplier)}><i className="fa fa-power-off" style={{ marginRight: '0.5rem', color: '#f59e0b' }}></i> {supplier.contact_status === 'inactive' ? 'Activate' : 'Deactivate'}</button>
                                                                <hr style={{ margin: '4px 0', border: 'none', borderTop: '1px solid #e2e8f0' }} />
                                                                <button onClick={() => openLedger(supplier)}><i className="fa fa-book" style={{ marginRight: '0.5rem', color: '#8b5cf6' }}></i> Ledger</button>
                                                                <button onClick={() => { setSelectedSupplier(supplier); setShowPurchasesModal(true); setOpenActionId(null); }}><i className="fa fa-shopping-cart" style={{ marginRight: '0.5rem', color: '#0891b2' }}></i> Purchases</button>
                                                                <button onClick={() => openDocModal(supplier)}><i className="fa fa-paperclip" style={{ marginRight: '0.5rem', color: '#dc2626' }}></i> Documents &amp; Note</button>
                                                            </div>,
                                                            document.body
                                                        )}

                                                    </div>
                                                </td>
                                                <td>{supplier.contact_id || '--'}</td>
                                                <td>
                                                    <div style={{ fontWeight: '500' }}>{supplier.supplier_business_name || supplier.business_name || '--'}</div>
                                                    {supplier.contact_status === 'inactive' && <div style={{ color: '#ef4444', fontSize: '0.8rem', fontWeight: '700', marginTop: '4px' }}>(Deactivated)</div>}
                                                </td>
                                                <td>
                                                    <div style={{ fontWeight: '500' }}>{supplier.name || '--'}</div>
                                                    {supplier.contact_status === 'inactive' && <div style={{ color: '#ef4444', fontSize: '0.8rem', fontWeight: '700', marginTop: '4px' }}>(Deactivated)</div>}
                                                </td>
                                                <td>{supplier.email || '--'}</td>
                                                <td>{supplier.tax_number || '--'}</td>
                                                <td>{supplier.pay_term_number ? `${supplier.pay_term_number} ${supplier.pay_term_type || ''}` : '--'}</td>
                                                <td>{supplier.opening_balance != null ? `$${Number(supplier.opening_balance).toFixed(2)}` : '--'}</td>
                                                <td>{supplier.advance_balance != null ? `$${Number(supplier.advance_balance).toFixed(2)}` : '--'}</td>
                                                <td>{supplier.created_at ? new Date(supplier.created_at).toLocaleDateString() : '--'}</td>
                                                <td>{[supplier.address_line_1, supplier.city, supplier.state].filter(Boolean).join(', ') || '--'}</td>
                                                <td>{supplier.mobile || '--'}</td>
                                                <td>{supplier.purchase_due != null ? `$${Number(supplier.purchase_due).toFixed(2)}` : (supplier.total_purchase_due != null ? `$${Number(supplier.total_purchase_due).toFixed(2)}` : '--')}</td>
                                                <td>{supplier.purchase_return != null ? `$${Number(supplier.purchase_return).toFixed(2)}` : (supplier.total_purchase_return_due != null ? `$${Number(supplier.total_purchase_return_due).toFixed(2)}` : '--')}</td>
                                                <td>{supplier.custom_field1 || '--'}</td>
                                                <td>{supplier.custom_field2 || '--'}</td>
                                                <td>{supplier.custom_field3 || '--'}</td>
                                                <td>{supplier.custom_field4 || '--'}</td>
                                                <td>{supplier.custom_field5 || '--'}</td>
                                            </tr>
                                        ));
                                    })()
                                )}
                            </tbody>
                        </table>
                    )}
            </div>

            {/* Pagination */}
            {filteredSuppliers.length > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', borderTop: '1px solid #e2e8f0', background: 'white' }}>
                    <div>
                        Showing {((currentPage - 1) * perPage) + 1} to {Math.min(currentPage * perPage, filteredSuppliers.length)} of {filteredSuppliers.length} entries
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                            className="pagination-btn"
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                        >
                            Previous
                        </button>
                        <span style={{ display: 'flex', alignItems: 'center', padding: '0 0.5rem' }}>
                            Page {currentPage} of {lastPage || 1}
                        </span>
                        <button
                            className="pagination-btn"
                            onClick={() => setCurrentPage(p => Math.min(lastPage, p + 1))}
                            disabled={currentPage === Math.ceil(filteredSuppliers.length / perPage)}
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}

            {/* ── View Modal ── */}
            {showViewModal && selectedSupplier && (
                <div className="modal-overlay" onClick={handleCloseModal}>
                    <div className="modal-container invoice-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>View Supplier — <span style={{ color: '#6366f1' }}>{selectedSupplier.supplier_business_name || selectedSupplier.business_name || selectedSupplier.name}</span></h2>
                            <button className="modal-close-btn" onClick={handleCloseModal}>✕</button>
                        </div>
                        <div className="modal-content invoice-content" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
                            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                {/* Main info row */}
                                <div className="customer-service-section">
                                    <div className="customer-info-box">
                                        <h3 style={{ fontSize: '1.05rem', fontWeight: '600', marginBottom: '0.75rem', borderBottom: '1px solid var(--etpos-border)', paddingBottom: '0.25rem', color: 'var(--etpos-primary)' }}>Basic Info</h3>
                                        <div className="info-item">
                                            <strong>Contact ID</strong>
                                            <div>{selectedSupplier.contact_id || '--'}</div>
                                        </div>
                                        <div className="info-item" style={{ marginTop: '0.5rem' }}>
                                            <strong>Full Name</strong>
                                            <div>{selectedSupplier.name || '--'}</div>
                                        </div>
                                        <div className="info-item" style={{ marginTop: '0.5rem' }}>
                                            <strong>Business Name</strong>
                                            <div>{selectedSupplier.supplier_business_name || selectedSupplier.business_name || '--'}</div>
                                        </div>
                                        <div className="info-item" style={{ marginTop: '0.5rem' }}>
                                            <strong>Tax Number</strong>
                                            <div>{selectedSupplier.tax_number || '--'}</div>
                                        </div>
                                    </div>
                                    <div className="service-info-box">
                                        <h3 style={{ fontSize: '1.05rem', fontWeight: '600', marginBottom: '0.75rem', borderBottom: '1px solid var(--etpos-border)', paddingBottom: '0.25rem', color: 'var(--etpos-primary)' }}>Account Status</h3>
                                        <div className="info-item">
                                            <strong>Status</strong>
                                            <div>
                                                <span style={{ 
                                                    padding: '0.2rem 0.6rem', 
                                                    borderRadius: '4px', 
                                                    fontSize: '0.75rem', 
                                                    fontWeight: '700',
                                                    backgroundColor: selectedSupplier.contact_status === 'inactive' ? '#fee2e2' : '#dcfce7',
                                                    color: selectedSupplier.contact_status === 'inactive' ? '#991b1b' : '#166534'
                                                }}>
                                                    {(selectedSupplier.contact_status || 'active').toUpperCase()}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="info-item" style={{ marginTop: '0.5rem' }}>
                                            <strong>Type / Sub Type</strong>
                                            <div>{selectedSupplier.type || 'Supplier'} {selectedSupplier.sub_type ? `/ ${selectedSupplier.sub_type}` : ''}</div>
                                        </div>
                                        <div className="info-item" style={{ marginTop: '0.5rem' }}>
                                            <strong>Added On</strong>
                                            <div>{selectedSupplier.created_at ? new Date(selectedSupplier.created_at).toLocaleDateString() : '--'}</div>
                                        </div>
                                    </div>
                                </div>

                                {/* Contact Details */}
                                <div className="invoice-section">
                                    <h3>Contact Details</h3>
                                    <div className="shipping-info-box" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' }}>
                                        <div className="info-item">
                                            <strong>Mobile</strong>
                                            <div>{selectedSupplier.mobile || '--'}</div>
                                        </div>
                                        <div className="info-item">
                                            <strong>Alternate Number</strong>
                                            <div>{selectedSupplier.alternate_number || '--'}</div>
                                        </div>
                                        <div className="info-item">
                                            <strong>Landline</strong>
                                            <div>{selectedSupplier.landline || '--'}</div>
                                        </div>
                                        <div className="info-item">
                                            <strong>Email</strong>
                                            <div>{selectedSupplier.email || '--'}</div>
                                        </div>
                                    </div>
                                </div>

                                {/* Financial Info */}
                                <div className="invoice-section">
                                    <h3>Financial Information</h3>
                                    <div className="shipping-info-box" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' }}>
                                        <div className="info-item">
                                            <strong>Opening Balance</strong>
                                            <div>{selectedSupplier.opening_balance != null ? `$${Number(selectedSupplier.opening_balance).toFixed(2)}` : '--'}</div>
                                        </div>
                                        <div className="info-item">
                                            <strong>Advance Balance</strong>
                                            <div>{selectedSupplier.advance_balance != null ? `$${Number(selectedSupplier.advance_balance).toFixed(2)}` : '--'}</div>
                                        </div>
                                        <div className="info-item">
                                            <strong>Current Balance</strong>
                                            <div style={{ fontWeight: 700, color: selectedSupplier.balance > 0 ? '#ef4444' : '#166534' }}>
                                                {`$${Number(selectedSupplier.balance || 0).toFixed(2)}`}
                                            </div>
                                        </div>
                                        <div className="info-item">
                                            <strong>Credit Limit</strong>
                                            <div>{selectedSupplier.credit_limit != null ? `$${Number(selectedSupplier.credit_limit).toFixed(2)}` : '--'}</div>
                                        </div>
                                        <div className="info-item">
                                            <strong>Total Purchase Due</strong>
                                            <div>{selectedSupplier.purchase_due != null ? `$${Number(selectedSupplier.purchase_due).toFixed(2)}` : (selectedSupplier.total_purchase_due != null ? `$${Number(selectedSupplier.total_purchase_due).toFixed(2)}` : '--')}</div>
                                        </div>
                                        <div className="info-item">
                                            <strong>Total Purchase Return Due</strong>
                                            <div>{selectedSupplier.purchase_return != null ? `$${Number(selectedSupplier.purchase_return).toFixed(2)}` : (selectedSupplier.total_purchase_return_due != null ? `$${Number(selectedSupplier.total_purchase_return_due).toFixed(2)}` : '--')}</div>
                                        </div>
                                        <div className="info-item">
                                            <strong>Pay Term</strong>
                                            <div>{selectedSupplier.pay_term_number ? `${selectedSupplier.pay_term_number} ${selectedSupplier.pay_term_type || ''}` : '--'}</div>
                                        </div>
                                    </div>
                                </div>

                                {/* Address Section */}
                                <div className="invoice-section">
                                    <h3>Address</h3>
                                    <div className="shipping-info-box" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' }}>
                                        <div className="info-item">
                                            <strong>Address Line 1</strong>
                                            <div>{selectedSupplier.address_line_1 || '--'}</div>
                                        </div>
                                        <div className="info-item">
                                            <strong>Address Line 2</strong>
                                            <div>{selectedSupplier.address_line_2 || '--'}</div>
                                        </div>
                                        <div className="info-item">
                                            <strong>City</strong>
                                            <div>{selectedSupplier.city || '--'}</div>
                                        </div>
                                        <div className="info-item">
                                            <strong>State</strong>
                                            <div>{selectedSupplier.state || '--'}</div>
                                        </div>
                                        <div className="info-item">
                                            <strong>Country</strong>
                                            <div>{selectedSupplier.country || '--'}</div>
                                        </div>
                                        <div className="info-item">
                                            <strong>Zip Code</strong>
                                            <div>{selectedSupplier.zip_code || '--'}</div>
                                        </div>
                                    </div>
                                </div>

                                {/* Custom Fields Section */}
                                {(selectedSupplier.custom_field1 || selectedSupplier.custom_field2 || selectedSupplier.custom_field3 || selectedSupplier.custom_field4 || selectedSupplier.custom_field5) && (
                                    <div className="invoice-section">
                                        <h3>Custom Fields</h3>
                                        <div className="shipping-info-box" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' }}>
                                            {selectedSupplier.custom_field1 && (
                                                <div className="info-item">
                                                    <strong>Custom Field 1</strong>
                                                    <div>{selectedSupplier.custom_field1}</div>
                                                </div>
                                            )}
                                            {selectedSupplier.custom_field2 && (
                                                <div className="info-item">
                                                    <strong>Custom Field 2</strong>
                                                    <div>{selectedSupplier.custom_field2}</div>
                                                </div>
                                            )}
                                            {selectedSupplier.custom_field3 && (
                                                <div className="info-item">
                                                    <strong>Custom Field 3</strong>
                                                    <div>{selectedSupplier.custom_field3}</div>
                                                </div>
                                            )}
                                            {selectedSupplier.custom_field4 && (
                                                <div className="info-item">
                                                    <strong>Custom Field 4</strong>
                                                    <div>{selectedSupplier.custom_field4}</div>
                                                </div>
                                            )}
                                            {selectedSupplier.custom_field5 && (
                                                <div className="info-item">
                                                    <strong>Custom Field 5</strong>
                                                    <div>{selectedSupplier.custom_field5}</div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="modal-btn close-btn" style={{ background: '#6366f1', color: 'white' }} onClick={() => { handleCloseModal(); handleEditSupplier(selectedSupplier.id); }}>Edit</button>
                            <button className="modal-btn close-btn" onClick={handleCloseModal}>Close</button>
                        </div>
                    </div>
                </div>
            )}

        {/* ── Pay Modal ── */}
        {showPayModal && selectedSupplier && (
            <div className="modal-overlay" onClick={() => setShowPayModal(false)}>
                <div className="modal-container" style={{ maxWidth: '700px', width: '95%', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
                    <div className="modal-header">
                        <h2>Add payment</h2>
                        <button className="modal-close-btn" onClick={() => setShowPayModal(false)}>✕</button>
                    </div>
                    <div style={{ padding: '1.5rem 2rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                        
                        {/* Top Info Cards */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                            <div style={{ background: 'var(--pay-card-bg, #f8fafc)', border: '1px solid var(--pay-card-border, #e2e8f0)', borderRadius: '4px', padding: '1rem', fontSize: '0.9rem', color: 'var(--pay-card-text, #1e293b)' }}>
                                <div style={{ marginBottom: '0.25rem' }}><strong>Supplier:</strong> {selectedSupplier.name || '--'}</div>
                                <div><strong>Business:</strong> {selectedSupplier.supplier_business_name || selectedSupplier.business_name || '--'}</div>
                            </div>
                            <div style={{ background: 'var(--pay-card-bg, #f8fafc)', border: '1px solid var(--pay-card-border, #e2e8f0)', borderRadius: '4px', padding: '1rem', fontSize: '0.9rem', color: 'var(--pay-card-text, #1e293b)', lineHeight: '1.5' }}>
                                <div><strong>Total Purchase:</strong> ${Number(selectedSupplier.total_purchase || 0).toFixed(2)}</div>
                                <div><strong>Total Paid:</strong> ${Number(selectedSupplier.total_paid || 0).toFixed(2)}</div>
                                <div><strong>Total Purchase Due:</strong> ${Number(selectedSupplier.total_purchase_due || selectedSupplier.purchase_due || 0).toFixed(2)}</div>
                                <div><strong>Credit Limit:</strong> ${selectedSupplier.credit_limit ? Number(selectedSupplier.credit_limit).toFixed(2) : '0.00'}</div>
                                <div><strong>Opening Balance:</strong> ${Number(selectedSupplier.opening_balance || 0).toFixed(2)}</div>
                                <div><strong>Opening Balance Due:</strong> ${Number(selectedSupplier.opening_balance || 0).toFixed(2)}</div>
                            </div>
                        </div>

                        {/* Form Grid */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
                            <div>
                                <label style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--pay-label-text, #1e293b)', display: 'block', marginBottom: '0.4rem' }}>Payment Method:*</label>
                                <div style={{ display: 'flex', border: '1px solid var(--pay-icon-border, #cbd5e1)', borderRadius: '4px', overflow: 'hidden' }}>
                                    <span style={{ padding: '0.5rem', background: 'var(--pay-icon-bg, #f1f5f9)', borderRight: '1px solid var(--pay-icon-border, #cbd5e1)', color: 'var(--pay-icon-color, #64748b)' }}><i className="fa fa-money-bill-wave"></i></span>
                                    <select style={{ flex: 1, padding: '0.5rem', border: 'none', outline: 'none', background: 'var(--pay-input-bg, white)', color: 'var(--pay-input-text, inherit)' }}>
                                        <option>Cash</option>
                                        <option>Card</option>
                                        <option>Check</option>
                                        <option>Bank Transfer</option>
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--pay-label-text, #1e293b)', display: 'block', marginBottom: '0.4rem' }}>Paid on:*</label>
                                <div style={{ display: 'flex', border: '1px solid var(--pay-icon-border, #cbd5e1)', borderRadius: '4px', overflow: 'hidden' }}>
                                    <span style={{ padding: '0.5rem', background: 'var(--pay-icon-bg, #f1f5f9)', borderRight: '1px solid var(--pay-icon-border, #cbd5e1)', color: 'var(--pay-icon-color, #64748b)' }}><i className="fa fa-calendar"></i></span>
                                    <input type="datetime-local" defaultValue={dateUtils.getChicagoISOString()} style={{ flex: 1, padding: '0.5rem', border: 'none', outline: 'none', background: 'var(--pay-input-bg, white)', color: 'var(--pay-input-text, inherit)' }} />
                                </div>
                            </div>
                            <div>
                                <label style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--pay-label-text, #1e293b)', display: 'block', marginBottom: '0.4rem' }}>Amount:*</label>
                                <div style={{ display: 'flex', border: '1px solid var(--pay-icon-border, #cbd5e1)', borderRadius: '4px', overflow: 'hidden' }}>
                                    <span style={{ padding: '0.5rem', background: 'var(--pay-icon-bg, #f1f5f9)', borderRight: '1px solid var(--pay-icon-border, #cbd5e1)', color: 'var(--pay-icon-color, #64748b)' }}><i className="fa fa-money-bill"></i></span>
                                    <input type="number" step="0.01" value={payAmount} onChange={e => setPayAmount(e.target.value)} style={{ flex: 1, padding: '0.5rem', border: 'none', outline: 'none', background: 'var(--pay-input-bg, white)', color: 'var(--pay-input-text, inherit)' }} />
                                </div>
                            </div>
                        </div>

                        {/* Bottom Form Grid */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '1rem' }}>
                            <div>
                                <label style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--pay-label-text, #1e293b)', display: 'block', marginBottom: '0.4rem' }}>Attach Document:</label>
                                <input type="file" style={{ width: '100%', fontSize: '0.85rem', color: 'var(--pay-input-text, inherit)' }} />
                                <div style={{ fontSize: '0.75rem', color: 'var(--pay-helper-text, #64748b)', marginTop: '0.5rem' }}>Allowed File: .pdf, .csv, .zip, .doc, .docx, .jpeg, .jpg, .png</div>
                            </div>
                            <div>
                                <label style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--pay-label-text, #1e293b)', display: 'block', marginBottom: '0.4rem' }}>Payment Account:</label>
                                <div style={{ display: 'flex', border: '1px solid var(--pay-icon-border, #cbd5e1)', borderRadius: '4px', overflow: 'hidden' }}>
                                    <span style={{ padding: '0.5rem', background: 'var(--pay-icon-bg, #f1f5f9)', borderRight: '1px solid var(--pay-icon-border, #cbd5e1)', color: 'var(--pay-icon-color, #64748b)' }}><i className="fa fa-university"></i></span>
                                    <select style={{ flex: 1, padding: '0.5rem', border: 'none', outline: 'none', background: 'var(--pay-input-bg, white)', color: 'var(--pay-input-text, inherit)' }}>
                                        <option>None</option>
                                        <option>Cash on Hand</option>
                                        <option>New Bank Account</option>
                                        <option>NSF Return</option>
                                        <option>Axis bank</option>
                                        <option>CASH ON HAND</option>
                                    </select>
                                </div>
                                <div style={{ marginTop: '1rem' }}>
                                    <label style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--pay-label-text, #1e293b)', display: 'block', marginBottom: '0.4rem' }}>Payment Note:</label>
                                    <textarea rows={2} value={payNote} onChange={e => setPayNote(e.target.value)} style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid var(--pay-icon-border, #cbd5e1)', borderRadius: '4px', fontSize: '0.9rem', resize: 'vertical', boxSizing: 'border-box', background: 'var(--pay-input-bg, white)', color: 'var(--pay-input-text, inherit)' }} placeholder="Optional note" />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer" style={{ padding: '1.25rem 2rem', display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                        <button style={{ padding: '0.6rem 1.75rem', background: '#4f46e5', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 700 }} onClick={() => { alert('Payment saved'); setShowPayModal(false); }}>Save</button>
                        <button style={{ padding: '0.6rem 1.75rem', background: '#334155', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 700 }} onClick={() => setShowPayModal(false)}>Close</button>
                    </div>
                </div>
            </div>
        )}

            {/* ── Ledger Modal ── */}
            {showLedgerModal && selectedSupplier && (
                <LedgerModal
                    sale={{ contact_id: selectedSupplier.id, customer: selectedSupplier.name }}
                    onClose={() => { setShowLedgerModal(false); setSelectedSupplier(null); }}
                />
            )}

            {/* ── Documents & Note Modal ── */}
            {showDocModal && selectedSupplier && (
                <div className="modal-overlay" onClick={() => setShowDocModal(false)}>
                    <div className="modal-container" style={{ maxWidth: '500px', width: '95%' }} onClick={e => e.stopPropagation()}>
                        <div className="modal-header"><h2>Documents &amp; Note — {selectedSupplier.name}</h2><button className="modal-close-btn" onClick={() => setShowDocModal(false)}>✕</button></div>
                        <div style={{ padding: '1.5rem 2rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            <div><label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Note</label>
                                <textarea rows={4} value={docNote} onChange={e => setDocNote(e.target.value)} style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: '0.9rem', resize: 'vertical' }} placeholder="Add a note..." /></div>
                            <div><label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Attach Document</label>
                                <input type="file" style={{ width: '100%' }} /></div>
                        </div>
                        <div className="modal-footer" style={{ borderTop: '1px solid #e2e8f0', padding: '1rem 2rem', display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                            <button style={{ padding: '0.5rem 1.25rem', background: '#6366f1', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }} onClick={() => { alert('Saved (demo)'); setShowDocModal(false); }}>Save</button>
                            <button style={{ padding: '0.5rem 1.25rem', background: '#64748b', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }} onClick={() => setShowDocModal(false)}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Purchases Modal ── */}
            {showPurchasesModal && selectedSupplier && (
                <ContactPurchasesModal
                    type="supplier"
                    contact={selectedSupplier}
                    onClose={() => setShowPurchasesModal(false)}
                />
            )}

            <ConfirmModal
                isOpen={confirmModal.isOpen}
                title={confirmModal.action === 'delete' ? 'Delete Supplier' : confirmModal.data?.contact_status === 'inactive' ? 'Activate Supplier' : 'Deactivate Supplier'}
                message={confirmModal.action === 'delete' ? 'Are you sure you want to delete this supplier?' : `Are you sure you want to ${confirmModal.data?.contact_status === 'inactive' ? 'activate' : 'deactivate'} this supplier?`}
                onConfirm={confirmAction}
                onCancel={() => setConfirmModal({ isOpen: false, action: '', data: null })}
                confirmText={confirmModal.action === 'delete' ? 'Delete' : confirmModal.data?.contact_status === 'inactive' ? 'Activate' : 'Deactivate'}
            />
        </div>
    );
};

export default Suppliers;
